import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth"; // Assuming you have an auth hook to get the UID
import { getUserReferrals, type ReferralDoc } from "@/lib/firebase";
import { ChevronLeft, User, Users, CheckCircle2, Clock, Trophy, AlertCircle, Loader2 } from "lucide-react";

export default function ReferralTracking() {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState<ReferralDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReferrals() {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      try {
        const data = await getUserReferrals(user.uid);
        setReferrals(data);
      } catch (error) {
        console.error("Failed to load referrals:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchReferrals();
  }, [user?.uid]);

  const completedCount = referrals.filter(r => r.status === "completed").length;
  const pendingCount = referrals.length - completedCount;
  const totalEarned = completedCount * 500; // 500 points per successful referral

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBEB] dark:bg-[#121212] font-sans">
      {/* Header */}
      <header className="px-4 py-4 flex items-center sticky top-0 z-30 bg-[#FFFBEB]/90 dark:bg-[#121212]/90 backdrop-blur-md">
        <button onClick={() => window.history.back()} className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95 transition-transform">
          <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
        </button>
        <h1 className="text-lg font-black text-slate-900 dark:text-white ml-4">Your Referrals</h1>
      </header>

      <div className="flex-1 px-5 pt-4 pb-12">
        {/* Stats Summary Cards */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white dark:bg-slate-900 rounded-[1.5rem] p-5 border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-2">
              <Trophy className="w-5 h-5" />
            </div>
            <p className="text-2xl font-black text-slate-900 dark:text-white">{totalEarned}</p>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Points Earned</p>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-[1.5rem] p-5 border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-2">
              <Users className="w-5 h-5" />
            </div>
            <p className="text-2xl font-black text-slate-900 dark:text-white">{referrals.length}</p>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Friends Invited</p>
          </div>
        </div>

        <h2 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-4 px-1">Tracking History</h2>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#991B1B] mb-4" />
            <p className="text-slate-500 font-medium">Loading your referrals...</p>
          </div>
        ) : referrals.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-8 border border-slate-100 dark:border-slate-800 shadow-sm text-center">
            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-bold text-slate-900 dark:text-white mb-2">No referrals yet</h3>
            <p className="text-sm text-slate-500">Share your link to start earning rewards!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {referrals.map((referral) => (
              <div key={referral.id} className="bg-white dark:bg-slate-900 rounded-[1.5rem] p-5 border border-slate-100 dark:border-slate-800 shadow-sm">

                {/* User Info Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">{referral.refereeName}</p>
                      <p className="text-xs text-slate-500">Joined {new Date(referral.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {referral.status === "completed" ? (
                    <span className="bg-green-100 text-green-700 text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> 500 Pts
                    </span>
                  ) : (
                    <span className="bg-amber-100 text-amber-700 text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Pending
                    </span>
                  )}
                </div>

                {/* Progress Steps */}
                <div className="pt-4 border-t border-slate-50 dark:border-slate-800 relative">
                  {/* Background connecting line */}
                  <div className="absolute left-[15px] top-[24px] bottom-2 w-0.5 bg-slate-100 dark:bg-slate-800 z-0"></div>

                  <div className="space-y-4 relative z-10">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${referral.steps.signedUp ? 'bg-[#991B1B] text-white' : 'bg-slate-100 text-slate-400'}`}>
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <p className={`text-sm font-bold ${referral.steps.signedUp ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>Signed Up</p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${referral.steps.addedProduct ? 'bg-[#991B1B] text-white' : 'bg-slate-100 text-slate-400'}`}>
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <p className={`text-sm font-bold ${referral.steps.addedProduct ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>Added First Product</p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${referral.steps.madeSale ? 'bg-[#991B1B] text-white' : 'bg-slate-100 text-slate-400'}`}>
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <p className={`text-sm font-bold ${referral.steps.madeSale ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>Recorded First Sale</p>
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}